"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { Subject } from "../../../../lib/subject-data"
import { teachers } from "../../../../lib/subject-data"

interface EditSubjectModalProps {
  isOpen: boolean
  onClose: () => void
  onUpdate: (subject: Subject) => void
  subject: Subject | null
}

export function EditSubjectModal({ isOpen, onClose, onUpdate, subject }: EditSubjectModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    teacher: teachers[0],
    description: "",
  })

  useEffect(() => {
    if (subject) {
      setFormData({
        name: subject.name,
        code: subject.code,
        teacher: subject.teacher,
        description: subject.description,
      })
    }
  }, [subject])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.code || !subject) {
      alert("Please fill in all required fields.")
      return
    }

    const updatedSubject: Subject = {
      ...subject,
      name: formData.name,
      code: formData.code,
      teacher: formData.teacher,
      teacherAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.teacher}`,
      description: formData.description,
    }

    onUpdate(updatedSubject)
    onClose()
  }

  if (!isOpen || !subject) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-bold text-white">Edit Subject</h3>
            <button 
              onClick={onClose} 
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editCourseName">
                    Subject Name
                  </label>
                  <Input
                    id="editCourseName"
                    name="courseName"
                    placeholder="e.g. Social Studies"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editCourseCode">
                      Course Code
                    </label>
                    <Input
                      id="editCourseCode"
                      name="courseCode"
                      placeholder="e.g. SOC-101"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editTeacher">
                      Assigned Teacher
                    </label>
                    <select
                      id="editTeacher"
                      name="teacher"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={formData.teacher}
                      onChange={(e) => setFormData({ ...formData, teacher: e.target.value })}
                    >
                      {teachers.map((teacher) => (
                        <option key={teacher} value={teacher}>
                          {teacher}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="editDescription">
                    Course Description
                  </label>
                  <textarea
                    id="editDescription"
                    name="description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of the curriculum..."
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-gray-300 hover:bg-gray-100 text-gray-700"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Update
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}